import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm, getFormValues } from 'redux-form';
import { Link as RouterLink } from 'react-router';
import { ButtonV2 } from '@zola/zola-ui';

import { RenderSubmitButton, RenderDropdownSelect, RenderField, RenderDatePicker, RenderTimePicker } from '../../../common/form';
import * as Validations from '../../../common/form/commonValidations';
import EventTypes from '../../events/util/EventTypes';
import NewTableFields from '../util/NewTableFields';
import { onboardingTableStep, addNewChart, setNewChartEvent, createAndFetchEvent, increaseStep, decreaseStep, onboardingSelectionStep } from '../../../../actions/SeatingChartActions';

import { formatEventDate } from '../../common/EventFormContainer';

import './Onboarding.styles.less';

const OnboardingForm = (props) => {
  const { allEvents, events, handleSubmit, pristine, submitting, valid, step, onDecStep, onIncStep, onOnboardingSelectionStep, newFormTables } = props;

  const selectEvents = [];
  const eventIds = [];

  events.forEach(event => eventIds.push(event.event));

  if (allEvents != null) {
    Object.keys(allEvents.byDate).forEach((eventGroup) => {
      allEvents.byDate[eventGroup].forEach((event) => {
        if (!eventIds.includes(`${event.id}`)) {
          selectEvents.push({
            title: event.name,
            value: event.id,
          });
        }
      });
    });
  }

  const hasEvents = selectEvents != null && selectEvents.length > 0;
  const decrementFunc = step === 2 ? onOnboardingSelectionStep : onDecStep;

  let numTables = 0;
  let totalNumSeats = 0;

  if (newFormTables != null) {
    newFormTables.new_tables.forEach((tables) => {
      numTables += tables.num_tables;
      totalNumSeats += tables.num_tables * tables.num_seats;
    });
  }

  const disabled = numTables > 100 || totalNumSeats > 750;

  const handleOnboard = (request) => {
    const { onOnboardingTableStep, onCreateSeatingChart, onSetNewChartEvent, event, onCreateEvent } = props;

    if (step === 0) {
      onOnboardingTableStep();
      const newChartEvent = {};
      newChartEvent.event_id = request.event;
      newChartEvent.name = allEvents.byId[request.event].name;
      onSetNewChartEvent(newChartEvent);
    } else if (step === 1) {
      const req = formatEventDate(request);
      onCreateEvent(req);
    } if (step === 2) {
      const newTables = [];

      if (request.new_tables != null && request.new_tables.length > 0) {
        let displayOrder = 1;

        request.new_tables.forEach((setOfTables) => {
          for (let i = 0; i < setOfTables.num_tables; i += 1) {
            newTables.push({ name: `Table ${displayOrder}`, num_seats: setOfTables.num_seats, shape: 1, display_order: displayOrder });
            displayOrder += 1;
          }
        });
      }

      const addNewTables = { new_tables: newTables, num_tables: newTables.length };

      onCreateSeatingChart(event, addNewTables);
    }
  };

  return (
    <div className="onboard">
      <div className="onboard__header">
        <h1 className="page-heading col-xs-12 col-sm-6 h1">Create a Seating Chart</h1>
      </div>
      <div className="onboarding">
        <div className="row">
          <form onSubmit={handleSubmit(handleOnboard)}>
            {step === 0 && hasEvents && (
              <div className="col-xs-12 col-sm-9 col-md-10 event-step step-0">
                <div className="onboarding__image">
                  <div className="image__eventPicture" />
                </div>
                <div className="onboarding-form">
                  <h4 className="onboarding-form__header">Which event would you like to create a seating chart for?</h4>
                  <div className="col-sm-6 onboarding-form__eventsField">
                    <Field
                      component={RenderDropdownSelect}
                      name="event"
                      options={selectEvents}
                      id="event-name"
                      label="Event Name"
                      required
                      validate={Validations.REQUIRED}
                    />
                  </div>
                  <div className="onboarding-form__newEventBtn">
                    <ButtonV2 variant="secondary" className="onboarding-form__addTablesBtn" onClick={onIncStep}>Create New Event</ButtonV2>
                  </div>
                  <div className="margin-top-2x submit-container">
                    <div className="margin-top-2x">
                      <RenderSubmitButton pristine={pristine} submitting={submitting} valid={valid} value="Continue" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(step === 1 || !hasEvents) && (
              <div className="col-xs-12 col-sm-9 col-md-10 event-step step-1">
                <div className="onboarding__image">
                  <p className="onboarding__backBtn" onClick={decrementFunc}>&lsaquo; BACK</p>
                  <div className="image__eventPicture" />
                </div>
                <div className="onboarding-form">
                  <h4 className="onboarding-form__header">Enter your event details below</h4>
                  <p>You can always edit this later.</p>
                  <div className="row">
                    <div className="col-sm-4">
                      <Field
                        component={RenderDropdownSelect}
                        name="type"
                        options={EventTypes}
                        id="event-type-2"
                        label="Event Type"
                        required
                        validate={Validations.REQUIRED}
                      />
                    </div>
                    <div className="col-sm-8">
                      <Field
                        component={RenderField}
                        name="name"
                        label="Event Name"
                        type="text"
                        id="event-name"
                        placeholder="Our Ceremony"
                        required
                        maxChars={100}
                        validate={[Validations.REQUIRED, Validations.MAX_LENGTH_100]}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-4">
                      <Field
                        component={RenderDatePicker}
                        name="date"
                        label="Date"
                        id="event-date"
                        required
                        validate={[Validations.REQUIRED, Validations.DATE, Validations.DATE_AFTER_NOW]}
                      />
                    </div>
                    <div className="col-sm-4">
                      <Field
                        component={RenderTimePicker}
                        name="start_at"
                        id="event-start-time"
                        label="Start Time"
                        required
                        validate={Validations.REQUIRED}
                      />
                    </div>
                    <div className="col-sm-4">
                      <Field
                        component={RenderTimePicker}
                        name="end_at"
                        id="event-end-time"
                        label="End Time"
                      />
                    </div>
                  </div>
                  <div className="margin-top-2x submit-container">
                    <div className="margin-top-2x">
                      <RenderSubmitButton pristine={pristine} submitting={submitting} valid={valid} value="Add Event & Continue" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {step >= 2 && (
              <div className="event-step step-2">
                <div className="onboarding__image">
                  <RouterLink className="onboarding__backBtn prefix zola-ui link link-arrow" to="/wedding/manage/events">Back</RouterLink>
                  <div className="image__tablePicture" />
                </div>
                <div className="onboarding-form">
                  <h4 className="onboarding-form__header">Add some tables to get started!</h4>
                  <p>You can add guests to your tables later.</p>
                  <div className="onboarding-form__newTableGroup">
                    <FieldArray
                      name="new_tables"
                      component={NewTableFields}
                      numTables={numTables}
                      totalNumSeats={totalNumSeats}
                      id="new-table-fields"
                      lockAxis="y"
                    />
                  </div>
                  <div className="onboarding-form__BtnGroup">
                    <ButtonV2 variant="secondary" className="onboarding-form__BtnGroup__skip" onClick={handleOnboard}>Do This Later</ButtonV2>
                    <div className="table-button">
                      <RenderSubmitButton className="onboarding-form__BtnGroup__add" pristine={pristine} disabled={disabled || submitting} submitting={submitting} valid={valid} value="Add Tables" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  newFormTables: getFormValues('OnboardingForm')(state),
  step: state.seatingChart.step,
  event: state.seatingChart.event,
  events: state.seatingChart.events,
  allEvents: state.events.events,
});

const mapDispatchToProps = dispatch => ({
  onOnboardingTableStep: () => dispatch(onboardingTableStep()),
  onCreateSeatingChart: (event, newTables) => dispatch(addNewChart(event, newTables)),
  onSetNewChartEvent: event => dispatch(setNewChartEvent(event)),
  onCreateEvent: req => dispatch(createAndFetchEvent(req)),
  onIncStep: () => dispatch(increaseStep()),
  onDecStep: () => dispatch(decreaseStep()),
  onOnboardingSelectionStep: () => dispatch(onboardingSelectionStep()),
});

OnboardingForm.propTypes = {
  step: PropTypes.number,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onDecStep: PropTypes.func,
  onIncStep: PropTypes.func,
  onOnboardingSelectionStep: PropTypes.func,
  newFormTables: PropTypes.shape({}),
  onOnboardingTableStep: PropTypes.func,
  onCreateSeatingChart: PropTypes.func,
  onSetNewChartEvent: PropTypes.func,
  event: PropTypes.shape({}),
  events: PropTypes.arrayOf(PropTypes.shape({})),
  allEvents: PropTypes.shape({}),
  onCreateEvent: PropTypes.func,
};

export default reduxForm({
  form: 'OnboardingForm'
})(connect(mapStateToProps, mapDispatchToProps)(OnboardingForm));



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/Onboarding/Onboarding.jsx